import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Gift, Plus, Minus, Loader2, Pencil, Save, Check, MessageSquare } from "lucide-react";
import { API } from "@/lib/api";
import { formatRewardSummary } from "@/lib/rewardDisplay";


export default function CustomerDetailPage() {
  const { id } = useParams();
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [adjOpen, setAdjOpen] = useState(false);
  const [adjPoints, setAdjPoints] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [adjLoading, setAdjLoading] = useState(false);
  const [adjMode, setAdjMode] = useState("add");

  const [rwdOpen, setRwdOpen] = useState(false);
  const [rwdValue, setRwdValue] = useState("");
  const [rwdLabel, setRwdLabel] = useState("Manual Reward");
  const [rwdLoading, setRwdLoading] = useState(false);

  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTitle, setMsgTitle] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [msgSuccess, setMsgSuccess] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [redeeming, setRedeeming] = useState({});

  const load = async () => {
    const { data } = await axios.get(`${API}/customers/${id}`, { headers: authHeaders() });
    setCustomer(data);
    setEditName(data.name || "");
    setEditEmail(data.email || "");
    setEditMobile(data.mobile_number || "");
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const handleAdjust = async () => {
    setAdjLoading(true);
    const pts = parseInt(adjPoints) || 0;
    const points = adjMode === "add" ? Math.abs(pts) : -Math.abs(pts);
    try {
      await axios.post(`${API}/customers/${id}/adjust-points`, { points, reason: adjReason }, { headers: authHeaders() });
      setAdjOpen(false);
      setAdjPoints("");
      setAdjReason("");
      load();
    } finally { setAdjLoading(false); }
  };

  const handleIssueReward = async () => {
    setRwdLoading(true);
    try {
      await axios.post(`${API}/customers/${id}/issue-reward`, { value_gbp: parseFloat(rwdValue), label: rwdLabel }, { headers: authHeaders() });
      setRwdOpen(false);
      setRwdValue("");
      setRwdLabel("Manual Reward");
      load();
    } finally { setRwdLoading(false); }
  };

  const handleSendMessage = async () => {
    setMsgLoading(true);
    setMsgError("");
    setMsgSuccess("");
    try {
      await axios.post(
        `${API}/customers/${id}/notify`,
        { title: msgTitle.trim(), body: msgBody.trim() },
        { headers: authHeaders() },
      );
      setMsgSuccess("Message sent");
      setMsgTitle("");
      setMsgBody("");
      setTimeout(() => {
        setMsgOpen(false);
        setMsgSuccess("");
      }, 800);
    } catch (err) {
      setMsgError(err.response?.data?.detail || "Failed to send message");
    } finally {
      setMsgLoading(false);
    }
  };

  const handleEditSave = async () => {
    setEditLoading(true);
    setEditError("");
    try {
      const updates = {};
      if (editName.trim()) updates.name = editName.trim();
      if (editEmail.trim()) updates.email = editEmail.trim();
      if (editMobile.trim()) updates.mobile_number = editMobile.trim();
      await axios.put(`${API}/customers/${id}`, updates, { headers: authHeaders() });
      setEditOpen(false);
      load();
    } catch (err) {
      setEditError(err.response?.data?.detail || "Failed to update");
    } finally { setEditLoading(false); }
  };

  const handleRedeem = async (rewardId) => {
    setRedeeming((r) => ({ ...r, [rewardId]: true }));
    try {
      await axios.put(`${API}/rewards/${rewardId}/redeem`, {}, { headers: authHeaders() });
      load();
    } finally {
      setRedeeming((r) => ({ ...r, [rewardId]: false }));
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-ink/40">Loading...</div>;
  if (!customer) return <div className="text-danger">Customer not found</div>;

  const progress = customer.total_points_earned && customer.vendor?.reward_threshold
    ? Math.min(100, Math.round((customer.point_balance / customer.vendor?.reward_threshold) * 100))
    : 0;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="customer-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-ink/50 gap-1.5 -ml-2">
          <ArrowLeft size={14} /> Back
        </Button>
        <div className="h-4 w-px bg-line" />
        <div>
          <h1 className="text-xl font-bold text-ink">{customer.name || "Unknown Customer"}</h1>
          <p className="text-xs text-ink/50">{customer.email || customer.mobile_number} · {customer.vendor_name || "—"}</p>
        </div>
        {customer.tier && <Badge variant="outline" className="ml-1 text-xs">{customer.tier}</Badge>}
        {customer.is_shadow_profile && <Badge variant="outline" className="text-xs bg-surface text-ink/50">Shadow Profile</Badge>}
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardContent className="px-4 py-3">
            <p className="text-xs text-ink/50">Point Balance</p>
            <p className="text-2xl font-bold text-ink tracking-tight">{customer.point_balance?.toLocaleString()}</p>
            <div className="w-full bg-purple-50 rounded-full h-1.5 mt-2">
              <div className="bg-purple-500 h-1.5 rounded-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardContent className="px-4 py-3">
            <p className="text-xs text-ink/50">Lifetime Points</p>
            <p className="text-2xl font-bold text-ink tracking-tight">{customer.total_points_earned?.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardContent className="px-4 py-3">
            <p className="text-xs text-ink/50">Active Rewards</p>
            <p className="text-2xl font-bold text-ink tracking-tight">{customer.active_rewards?.length || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Manual Actions */}
      <div className="flex gap-2 flex-wrap">
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" data-testid="edit-customer-button">
              <Pencil size={12} /> Edit Details
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Edit Customer Details</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Name</Label>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Full name" data-testid="edit-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Email</Label>
                <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder="email@example.com" data-testid="edit-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Mobile Number</Label>
                <Input value={editMobile} onChange={(e) => setEditMobile(e.target.value)} placeholder="+447700900000" data-testid="edit-mobile-input" />
              </div>
              {editError && <div className="px-3 py-2 rounded-md bg-danger/10 border border-danger/30 text-danger text-sm" data-testid="edit-error">{editError}</div>}
              <Button onClick={handleEditSave} disabled={editLoading} data-testid="edit-save-button" className="w-full bg-purple-500 hover:bg-purple-600 text-white gap-1.5">
                {editLoading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" data-testid="adjust-points-button">
              <Plus size={12} /> Adjust Points
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Adjust Points</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button type="button" variant={adjMode === "add" ? "default" : "outline"} onClick={() => setAdjMode("add")} className="flex-1 h-9 gap-1.5 text-sm" data-testid="adj-mode-add">
                  <Plus size={14} /> Add Points
                </Button>
                <Button type="button" variant={adjMode === "deduct" ? "default" : "outline"} onClick={() => setAdjMode("deduct")} className="flex-1 h-9 gap-1.5 text-sm" data-testid="adj-mode-deduct">
                  <Minus size={14} /> Deduct Points
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Points</Label>
                <Input type="number" min="1" value={adjPoints} onChange={(e) => setAdjPoints(e.target.value)} placeholder="e.g. 100" data-testid="adj-points-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Reason</Label>
                <Input value={adjReason} onChange={(e) => setAdjReason(e.target.value)} placeholder="e.g. Goodwill gesture, correction" data-testid="adj-reason-input" />
              </div>
              <Button onClick={handleAdjust} disabled={adjLoading || !adjPoints || !adjReason} data-testid="adj-submit" className="w-full bg-purple-500 hover:bg-purple-600 text-white">
                {adjLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                {adjMode === "add" ? "Add" : "Deduct"} {adjPoints || 0} Points
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={rwdOpen} onOpenChange={setRwdOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" data-testid="issue-reward-button">
              <Gift size={12} /> Issue Reward
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Issue Manual Reward</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Reward Value (£)</Label>
                <Input type="number" min="0.01" step="0.01" value={rwdValue} onChange={(e) => setRwdValue(e.target.value)} placeholder="e.g. 5.00" data-testid="rwd-value-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Label</Label>
                <Input value={rwdLabel} onChange={(e) => setRwdLabel(e.target.value)} placeholder="Manual Reward" data-testid="rwd-label-input" />
              </div>
              <Button onClick={handleIssueReward} disabled={rwdLoading || !rwdValue} data-testid="rwd-submit" className="w-full bg-success hover:bg-success/90 text-white">
                {rwdLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                Issue £{rwdValue || "0"} Reward
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={msgOpen} onOpenChange={(open) => { setMsgOpen(open); setMsgError(""); setMsgSuccess(""); }}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" data-testid="send-message-button">
              <MessageSquare size={12} /> Send Message
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Send Message</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-ink/50">
                Sends a personal notification via web push and wallet (Apple / Google) when available.
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Title</Label>
                <Input
                  value={msgTitle}
                  onChange={(e) => setMsgTitle(e.target.value)}
                  placeholder={customer.vendor_name ? `${customer.vendor_name} update` : "Notification title"}
                  data-testid="msg-title-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest">Message</Label>
                <Input
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  placeholder="e.g. 20% off today only"
                  data-testid="msg-body-input"
                />
              </div>
              {msgError && (
                <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-danger text-sm" data-testid="msg-error">
                  {msgError}
                </div>
              )}
              {msgSuccess && (
                <div className="px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-success text-sm" data-testid="msg-success">
                  {msgSuccess}
                </div>
              )}
              <Button
                onClick={handleSendMessage}
                disabled={msgLoading || !msgTitle.trim() || !msgBody.trim()}
                data-testid="msg-submit"
                className="w-full bg-purple-500 hover:bg-purple-600 text-white gap-1.5"
              >
                {msgLoading ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
                Send Message
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Active Rewards */}
      {customer.active_rewards?.length > 0 && (
        <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
          <CardHeader className="pb-2 pt-4 px-5"><CardTitle className="text-sm font-semibold">Active Rewards</CardTitle></CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="space-y-2">
              {customer.active_rewards.map((r) => {
                const preview = formatRewardSummary(r);
                return (
                <div key={r.id} className="flex items-center justify-between px-4 py-3 bg-purple-100 border border-success/30 rounded-lg" data-testid={`reward-row-${r.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <Gift size={16} className="text-ink shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-success truncate">{preview.summary}</p>
                      <p className="text-xs text-ink truncate">
                        {preview.title !== preview.summary ? `${preview.title} · ` : ""}
                        Expires {r.expiry_date?.slice(0, 10) || "N/A"}
                      </p>
                      {preview.reward_type === "item_choice" && preview.item_options?.length > 0 && (
                        <p className="text-[11px] text-success/80 truncate">Options: {preview.item_options.join(", ")}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleRedeem(r.id)}
                    disabled={!!redeeming[r.id]}
                    data-testid={`redeem-reward-${r.id}`}
                    className="h-8 text-xs gap-1.5 bg-success hover:bg-success/90 text-white shrink-0"
                  >
                    {redeeming[r.id] ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    Redeem
                  </Button>
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card className="bg-white border border-ink/10 shadow-sm rounded-2xl">
        <CardHeader className="pb-2 pt-4 px-5"><CardTitle className="text-sm font-semibold">Recent Transactions</CardTitle></CardHeader>
        <CardContent className="px-0 pb-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-surface">
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Store</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Amount</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Points</TableHead>
                <TableHead className="text-xs uppercase tracking-widest text-ink/50">Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!customer.recent_transactions?.length ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-ink/40 text-sm">No transactions yet</TableCell></TableRow>
              ) : (
                customer.recent_transactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs ${t.type === "earn" ? "bg-purple-50 text-purple-700 border-purple-200" : t.type?.includes("manual") ? "bg-purple-50 text-ink border-warning/40" : "bg-purple-100 text-purple-700 border-purple-200"}`}>
                        {t.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-mono">{t.store_id}</TableCell>
                    <TableCell className="font-mono text-sm">{t.total_amount_gbp > 0 ? `£${t.total_amount_gbp?.toFixed(2)}` : "—"}</TableCell>
                    <TableCell className={`font-mono text-sm font-semibold ${t.points_earned >= 0 ? "text-ink" : "text-danger"}`}>
                      {t.points_earned >= 0 ? `+${t.points_earned}` : t.points_earned}
                    </TableCell>
                    <TableCell className="text-xs text-ink/40">{t.timestamp?.slice(0, 16).replace("T", " ")}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
